import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { Button, Image } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import Paginador, { PaginationProvider } from 'react-bootstrap-table2-paginator'
import ReactDragListView from 'react-drag-listview'
import { useSelector } from 'react-redux'
import { useTheme } from 'styled-components'

import { useGlobal } from '../../stores/useGlobal'
import { setaDropdown } from '../../utils/alternanciaIcones'
import GirarSetaDropdown from '../../utils/girarSetaDropdown'
import PaginacaoTabela from '../../utils/paginacaoTabela'
import BarraDadosCliente from '../componentesModais/barraDadosCliente/barraDadosCliente'
import BarraFiltragem from '../componentesModais/barraFiltragem/barraFiltragem'
import BarraTabelaLegenda from '../tabelaSelecao/barraTabelaLegenda'

/**
 * @typedef {Object} Pagination
 * @property {number} paginaAtual
 * @property {number} valorLimite
 * @property {number} totalPaginas
 * @property {number} totalItens
 * @property {number} contador
 * @property {(pagina: number) => void} trocarPagina
 * @property {(newPage: number) => void} onPageChange
 * @property {(fromIndex?: number, toIndex?: number) => void} [onDragEnd]
 */

/**
 * @typedef {Object} Selection
 * @property {"radio" | "checkbox"} modeSelection
 * @property {string[] | number[]} itensSelecionados
 * @property {(row: any, isSelect: boolean, rowIndex: number) => void} handleOnSelect
 * @property {(isSelectAll: boolean, rows: any[]) => void} [handleOnSelectAll]
 * @property {() => void} [onClickSelecaoParqueTodo]
 */

/**
 * @typedef {Object} TableProps
 * @property {Object[]} itens
 * @property {Object[]} colunas
 * @property {boolean} [spinAtivo]
 * @property {boolean} [showCaption]
 * @property {(show: boolean) => void} [setShowCaption]
 * @property {boolean} [enableDrag]
 * @property {boolean} [revelarContador]
 * @property {Pagination} pagination
 * @property {Selection} [selection]
 * @property {() => number} getTotalItensNaPagina
 * @property {() => number} getLimiteNaPagina
 * @property {(row: any) => React.ReactNode} [detalhamentLinha]
 * @property {boolean} [permissaoDetalhamentoLinha]
 * @property {string} keyField
 * @property {boolean} [clickToSelect]
 */

/**
 * @typedef {Object} TabelaComLegendaProps
 * @property {Object[]} itens
 * @property {string} keyField
 * @property {Object[]} colunas
 * @property {Object} selecaoLinhas
 * @property {Object} itemExpandido
 * @property {boolean} [showCaption]
 * @property {(show: boolean) => void} [setShowCaption]
 * @property {boolean} [spinAtivo]
 * @property {boolean} [revelarBarraFiltro]
 * @property {number} [totalItens]
 * @property {() => number} getTotalItensNaPagina
 * @property {Object} theme
 * @property {Object} paginationTableProps
 */

/**
 * @param {TabelaComLegendaProps} props
 */
function TabelaComLegenda({
  itens,
  colunas,
  selecaoLinhas,
  itemExpandido,
  spinAtivo,
  showCaption,
  setShowCaption,
  revelarBarraFiltro,
  totalItens,
  getTotalItensNaPagina,
  theme,
  paginationTableProps,
  keyField
}) {
  const { modoSelecaoSimcard } = useSelector((state) => state.manejadorState)

  const { selecionarParqueInteiro } = useGlobal()

  const parqueTodoSelecionado = modoSelecaoSimcard || selecionarParqueInteiro

  const itensComId = itens.map((item) => ({
    ...item,
    [keyField]: item?.[keyField]
  }))

  return (
    <>
      {showCaption && !spinAtivo && (
        <BarraTabelaLegenda
          setShowCaption={setShowCaption}
          onClick={selecaoLinhas.onClickSelecaoParqueTodo}
          parqueTodoSelecionado={parqueTodoSelecionado}
          filtros={revelarBarraFiltro}
          totalItens={totalItens}
          getTotalItensNaPagina={getTotalItensNaPagina}
        />
      )}

      <BootstrapTable
        classes="tabela"
        condensed
        keyField={keyField}
        data={itensComId}
        columns={colunas}
        selectRow={selecaoLinhas}
        expandRow={itemExpandido}
        bootstrap4
        bordered={false}
        noDataIndication={!spinAtivo && 'Nenhum item encontrado'}
        rowStyle={() =>
          parqueTodoSelecionado ? { backgroundColor: theme.cor7 } : {}
        }
        {...paginationTableProps}
      />
    </>
  )
}

const motionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0 },
  transition: { duration: 0.3 }
}

/**
 * @param {TableProps} props
 */
function Table({
  itens,
  colunas,
  spinAtivo,
  showCaption,
  setShowCaption,
  enableDrag = false,
  revelarContador,
  pagination,
  selection,
  getTotalItensNaPagina,
  getLimiteNaPagina,
  detalhamentLinha,
  permissaoDetalhamentoLinha,
  keyField,
  clickToSelect = false,
  revelarBarraFiltro
}) {
  const { modoSelecaoSimcard } = useSelector((state) => state.manejadorState)

  const { selecionarParqueInteiro: selecionarParqueInteiroZustand } =
    useGlobal()

  const parqueTodoSelecionado =
    modoSelecaoSimcard || selecionarParqueInteiroZustand

  const theme = useTheme()

  const [expandedRows, setExpandedRows] = useState([])

  const handleOnExpand = (row, isExpand) => {
    setExpandedRows((prev) =>
      isExpand
        ? [...prev, row[keyField]]
        : prev.filter((id) => id !== row[keyField])
    )

    GirarSetaDropdown(row[keyField])
  }

  const detalhamentLinhaComAnimacao = (row) => {
    return (
      <AnimatePresence>
        {expandedRows.includes(row[keyField]) && (
          <motion.div key={row[keyField]} {...motionProps}>
            {detalhamentLinha(row)}
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  const itemExpandido = {
    className: 'fundo-cor-1',
    expanded: expandedRows,
    renderer: detalhamentLinhaComAnimacao,
    onExpand: handleOnExpand,
    expandHeaderColumnRenderer: () => (
      <div className="cabecalho-linha-expandida" />
    ),
    expandColumnRenderer: (rowKey) => {
      return (
        <Button className="botao-expandir-row">
          <Image
            id={rowKey.rowKey}
            className="icone-botao-expandir-row nao-selecionavel"
            src={setaDropdown()}
          />
        </Button>
      )
    },
    showExpandColumn: permissaoDetalhamentoLinha,
    expandByColumnOnly: true,
    headerClasses: 'tabela-coluna-primeira-header'
  }

  const opcoesPaginacao = {
    page: pagination.paginaAtual,
    showTotal: true,
    custom: true,
    sizePerPage: pagination.valorLimite,
    firstPageTitle: 'Primeira',
    lastPageTitle: 'Última',
    nextPageTitle: 'Próxima',
    prePageTitle: 'Anterior',
    onPageChange: (newPage) => pagination.trocarPagina(newPage)
  }

  let selecaoLinhas
  if (selection?.modeSelection === 'radio') {
    selecaoLinhas = {
      mode: 'radio',
      clickToSelect: clickToSelect,
      selected: selection.itensSelecionados,
      onSelect: selection.handleOnSelect,
      bgColor: theme.cor7,
      selectionRenderer: ({ mode, ...rest }) => {
        delete rest.rowIndex

        return (
          <label className="label-checkbox-cliente-virtueyes fonte-cor-1">
            <input
              type={mode}
              id="CLIENTEVEYE"
              name="CLIENTE"
              readOnly
              {...rest}
            />
            <span className="checkbox-cliente-virtueyes radio-button-cor-1" />
          </label>
        )
      }
    }
  } else if (selection?.modeSelection === 'checkbox') {
    selecaoLinhas = {
      mode: 'checkbox',
      clickToSelect: clickToSelect,
      hideSelectColumn: parqueTodoSelecionado,
      hideSelectAll: parqueTodoSelecionado,
      selected: selection.itensSelecionados ?? [],
      onSelect: selection.handleOnSelect,
      onSelectAll: selection.handleOnSelectAll,
      onClickSelecaoParqueTodo: selection.onClickSelecaoParqueTodo,
      bgColor: theme.cor7,
      selectionRenderer: ({ mode, rowIndex, ...rest }) => (
        <React.Fragment>
          <input
            id={`input-${rowIndex}`}
            readOnly
            type={mode}
            className="input-checkbox-simcard"
            {...rest}
          />
          <label className="label-checkbox-simcard" />
        </React.Fragment>
      ),
      selectionHeaderRenderer: ({ mode, ...rest }) => {
        delete rest.indeterminate
        if (rest.checked && !showCaption && setShowCaption) {
          setShowCaption(true)
        }

        return (
          <React.Fragment>
            <input
              type={mode}
              className="input-checkbox-header-simcard"
              onChange={() => setShowCaption(rest.checked)}
              readOnly
              {...rest}
            />
            <label className="label-checkbox-header-simcard" />
          </React.Fragment>
        )
      }
    }
  } else {
    selecaoLinhas = undefined
  }

  return (
    <>
      <BarraDadosCliente />
      <BarraFiltragem revelarBarraFiltro={revelarBarraFiltro} />
      <PaginationProvider pagination={Paginador(opcoesPaginacao)}>
        {({ paginationProps, paginationTableProps }) => (
          <>
            {enableDrag ? (
              <ReactDragListView.DragColumn
                onDragEnd={pagination.onDragEnd}
                nodeSelector="th"
                ignoreSelector="th.selection-cell-header, th.expand-cell-header"
              >
                <TabelaComLegenda
                  itens={itens}
                  keyField={keyField}
                  colunas={colunas}
                  selecaoLinhas={selecaoLinhas}
                  itemExpandido={itemExpandido}
                  showCaption={showCaption}
                  setShowCaption={setShowCaption}
                  spinAtivo={spinAtivo}
                  revelarBarraFiltro={revelarBarraFiltro}
                  totalItens={pagination.totalItens}
                  getTotalItensNaPagina={getTotalItensNaPagina}
                  theme={theme}
                  paginationTableProps={paginationTableProps}
                />
              </ReactDragListView.DragColumn>
            ) : (
              <TabelaComLegenda
                itens={itens}
                keyField={keyField}
                colunas={colunas}
                selecaoLinhas={selecaoLinhas}
                itemExpandido={itemExpandido}
                showCaption={showCaption}
                setShowCaption={setShowCaption}
                spinAtivo={spinAtivo}
                revelarBarraFiltro={revelarBarraFiltro}
                totalItens={pagination.totalItens}
                getTotalItensNaPagina={getTotalItensNaPagina}
                theme={theme}
                paginationTableProps={paginationTableProps}
              />
            )}

            {revelarContador && (
              <PaginacaoTabela
                setPaginaAtual={pagination.trocarPagina}
                totalPaginas={pagination.totalPaginas}
                totalItens={pagination.totalItens}
                valorLimite={pagination.valorLimite}
                paginaAtual={pagination.paginaAtual}
                page={paginationProps.page}
                onPageChange={paginationProps.onPageChange}
              />
            )}
          </>
        )}
      </PaginationProvider>
      {revelarContador ? (
        <div className="itens-exibidos-tabela fonte-cor-1">
          Mostrando{' '}
          <span className="label-tabela-itens-exibidos">
            {pagination.contador + 1}
          </span>
          -
          <span className="label-tabela-itens-exibidos">
            {getLimiteNaPagina()}
          </span>{' '}
          de{' '}
          <span className="label-tabela-itens-exibidos">
            {pagination.totalItens}
          </span>
        </div>
      ) : (
        <div className="itens-exibidos-tabela fonte-cor-1">Carregando...</div>
      )}
    </>
  )
}

export default Table
